/** Panel grafikleri — scatter / chart kart SSOT (açık tema) */

import { PANEL } from "./panelTokens";
import { panelTableCardSx } from "./panelCardSx";

export const PANEL_CHART = {
  axis: PANEL.text.muted,
  axisLine: PANEL.border,
  grid: PANEL.fill.hoverAlt,
  legend: PANEL.text.body,
  tooltipBg: PANEL.surface,
  tooltipBorder: PANEL.border,
  tooltipShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
  selectedStroke: PANEL.accent.main,
};

export const panelAgeScoreScatterCardSx = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: PANEL.radius.cardMd,
  border: `1px solid ${PANEL.border}`,
  bgcolor: PANEL.surface,
  boxShadow: PANEL.shadow.card,
  p: 1.5,
  transition: "box-shadow 0.2s ease, border-color 0.2s ease",
  "&:hover": {
    boxShadow: PANEL.shadow.cardHover,
    borderColor: PANEL.accent.softBorder22,
  },
};

export const panelScatterPanelSx = (compact = false) => ({
  ...panelAgeScoreScatterCardSx,
  p: compact ? 1.25 : 1.5,
  minHeight: compact ? 200 : 320,
});

export const panelScatterGoToFormButtonSx = {
  flexShrink: 0,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.7rem",
  py: 0.5,
  px: 1,
  borderRadius: PANEL.radius.chip,
  bgcolor: PANEL.accent.main,
  color: PANEL.accent.contrast,
  boxShadow: "none",
  "&:hover": {
    bgcolor: PANEL.accent.hover,
    boxShadow: PANEL.accent.shadow25,
  },
};

export const panelScatterNavButtonSx = {
  flexShrink: 0,
  alignSelf: "center",
  color: PANEL.text.body,
  border: `1px solid ${PANEL.border}`,
  bgcolor: PANEL.surface,
  borderRadius: PANEL.radius.chip,
  "&:hover": {
    bgcolor: PANEL.fill.muted,
    borderColor: PANEL.fill.hover,
  },
  "&.Mui-disabled": {
    opacity: 0.4,
    color: PANEL.text.label,
  },
};

export const panelScatterExpandButtonSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: PANEL.radius.input,
  px: 2.5,
  color: PANEL.text.body,
  borderColor: PANEL.border,
  bgcolor: PANEL.surface,
  "&:hover": {
    borderColor: PANEL.accent.softBorder,
    bgcolor: PANEL.accent.soft,
  },
};

/** Form analitik — yaş-skor bölüm kabuğu */
export const panelAgeScoreScatterSectionSx = {
  ...panelTableCardSx,
  overflow: "hidden",
  p: { xs: 2, md: 2.5 },
  mb: 2.5,
};

export const panelAgeScoreScatterSectionHeaderSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.5,
  mb: 2.5,
  pb: 2,
  borderBottom: `1px solid ${PANEL.border}`,
};

export const panelAgeScoreScatterSectionIconSx = {
  width: 44,
  height: 44,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  bgcolor: PANEL.blue.soft12,
  border: `1px solid ${PANEL.blue.soft25}`,
  color: PANEL.blue.main,
  "& .MuiSvgIcon-root": { fontSize: 22 },
};

export const panelAgeScoreScatterRepeatAccentSx = (color) => ({
  flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" },
  minWidth: 0,
  borderLeft: `3px solid ${color}`,
  pl: 0.5,
});

/** Dashboard — dosya × cinsiyet heatmap (açık tema) */
export const PANEL_GENDER = {
  male: PANEL.blue.main,
  maleText: PANEL.blue.text,
  female: "#ec4899",
  femaleText: "#9d174d",
  femaleSoft: "rgba(236, 72, 153, 0.1)",
};

function lerpChannel(a, b, t) {
  return Math.round(a + (b - a) * t);
}

/** Sütun içi yoğunluk: açık zeminde erkek mavi, kadın pembe. */
export function panelGenderHeatmapCellBackground(count, maxForColumn, gender) {
  if (!count || count <= 0) return PANEL.fill.muted;
  const ratio = maxForColumn > 0 ? count / maxForColumn : 0;
  const t = ratio ** 0.5;
  if (gender === "male") {
    return `rgb(${lerpChannel(239, 59, t)}, ${lerpChannel(246, 130, t)}, ${lerpChannel(255, 246, t)})`;
  }
  return `rgb(${lerpChannel(253, 236, t)}, ${lerpChannel(242, 72, t)}, ${lerpChannel(248, 153, t)})`;
}

export function panelGenderHeatmapCellTextColor(count, maxForColumn, gender) {
  if (!count || count <= 0) return PANEL.text.muted;
  const ratio = maxForColumn > 0 ? count / maxForColumn : 0;
  const t = ratio ** 0.5;
  if (t >= 0.55) return PANEL.surface;
  return gender === "male" ? PANEL_GENDER.maleText : PANEL_GENDER.femaleText;
}

export const panelHeatmapTitleSx = {
  fontWeight: 700,
  color: PANEL.text.primary,
  flex: "1 1 auto",
  minWidth: 0,
  fontSize: "1.05rem",
  lineHeight: 1.3,
};

export const panelHeatmapTotalBadgeSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  px: 1.5,
  py: 0.75,
  borderRadius: PANEL.radius.cardMd,
  bgcolor: PANEL.indigo.soft,
  border: `1px solid ${PANEL.indigo.softBorder}`,
  minWidth: 84,
  flexShrink: 0,
};

export const panelHeatmapTotalBadgeLabelSx = {
  color: PANEL.text.muted,
  display: "block",
  lineHeight: 1.1,
};

export const panelHeatmapTotalBadgeValueSx = {
  fontWeight: 700,
  color: PANEL.indigo.text,
  lineHeight: 1.2,
};

export const panelHeatmapEmptyWrapSx = {
  height: 220,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: 1,
};

export const panelHeatmapHintSx = {
  mt: 2,
  p: 1.5,
  borderRadius: PANEL.radius.input,
  display: "flex",
  alignItems: "flex-start",
  gap: 1.25,
  border: `1px solid ${PANEL.blue.soft25}`,
  borderLeft: `3px solid ${PANEL.blue.main}`,
  bgcolor: PANEL.blue.soft,
};

export const panelHeatmapHintTextSx = {
  color: PANEL.text.body,
  lineHeight: 1.65,
  fontSize: "0.8125rem",
  fontWeight: 500,
};

export const panelHeatmapRowLabelSx = {
  color: PANEL.text.body,
  fontWeight: 600,
  pr: 1,
  display: "flex",
  alignItems: "center",
  wordBreak: "break-word",
};

export const panelHeatmapTotalHeaderSx = {
  color: PANEL.accent.text,
  fontWeight: 700,
  textAlign: "center",
  px: 0.5,
  pb: 1,
  wordBreak: "break-word",
};

export const panelHeatmapTotalCellSx = {
  minHeight: 44,
  borderRadius: PANEL.radius.chip,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: PANEL.accent.soft,
  border: `1px solid ${PANEL.accent.softBorder}`,
};

export const panelHeatmapGrandTotalCellSx = {
  ...panelHeatmapTotalCellSx,
  bgcolor: PANEL.accent.main,
  border: `1px solid ${PANEL.accent.hover}`,
};
